import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useAuth as useOidcAuth } from 'react-oidc-context'
import type { AuthBridge } from './auth-bridge'
import { setAuthBridge } from './auth-bridge'
import { setAuthErrorState } from './auth-error-store'

// The single, explicit seam that lets the (non-React) Axios client reach the
// provider-owned OIDC session (ADR-0011). This component renders nothing;
// it registers ONE bridge whose methods read the LATEST OIDC + query-client via refs,
// and clears the bridge on unmount. It is NOT a second session/callback owner and
// stores no tokens — every operation delegates to the `AuthProvider`.

const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const

export function AuthBridgeRegistrar() {
	const oidc = useOidcAuth()
	const queryClient = useQueryClient()

	// Keep refs current so the stable bridge (registered once) always operates on the
	// live OIDC user / query client rather than a captured stale render.
	const oidcRef = useRef(oidc)
	oidcRef.current = oidc
	const queryClientRef = useRef(queryClient)
	queryClientRef.current = queryClient

	useEffect(() => {
		const bridge: AuthBridge = {
			getAccessToken: () => {
				const user = oidcRef.current.user
				if (!user || user.expired) return undefined
				return user.access_token
			},
			renew: async () => {
				try {
					const renewed = await oidcRef.current.signinSilent()
					return renewed?.access_token ?? null
				} catch {
					return null
				}
			},
			clearSession: async () => {
				// Clear Atlas `/users/auth/me` state and remove the provider-owned OIDC user.
				// Does NOT touch the auth-error store, so a caller may set an inactive/
				// terminal state immediately afterward.
				queryClientRef.current.removeQueries({ queryKey: AUTH_ME_QUERY_KEY })
				try {
					await oidcRef.current.removeUser()
				} catch {
					// best-effort local cleanup
				}
			},
			startLogin: () => {
				void oidcRef.current.signinRedirect()
			},
			setAuthErrorState: (state) => {
				setAuthErrorState(state)
			},
		}
		setAuthBridge(bridge)
		return () => {
			setAuthBridge(null)
		}
	}, [])

	return null
}
